import { useBottomSheet } from '../../../../../state-management/contexts/BottomSheetContext'
import { useToast } from '../../../../../state-management/contexts/ToastContext'
import {
  ExecutionLearnMoreReason,
  OnboardingStatus,
  SubActionDisabledReason
} from '../../../../../types/mf-execution.types'
import { LumpsumRecommendationActionType } from '../../lumpsum/api/common.types'
import { isPlanFullSell } from '../../utils/planExecutionUtils'
import {
  postPlanCta,
  toastFundExecutionError,
  useFetchFundExecutionWithLoading,
  type PlanFundActionHookOptions
} from '../fundExecutionHelpers'
import { RegularFundNotSupportedBottomSheet } from '../RegularFundNotSupportedBottomSheet'
import { EventNames } from '../../../../../types/misc.types'
import { trackEvent } from '../../../../../utils/appBridge'
import { PlansExecutionEventIds, PlansScreen } from '../../analytics/plansEvents'

export type PlanSellActionInput = {
  isin: string
  amount: number
  fundName?: string
  currentValue?: number
  isFullWithdrawal?: boolean
}

export function usePlanSellAction({
  transactionCta,
  isRecommendationSheetSource = false,
  analyticsScreen = PlansScreen.LumpsumAllocationRecommendation
}: PlanFundActionHookOptions) {
  const { showToast } = useToast()
  const {
    openBottomSheet,
    closeBottomSheet,
    openNestedBottomSheet,
    closeNestedBottomSheet
  } = useBottomSheet()
  const openErrorSheet = isRecommendationSheetSource ? openNestedBottomSheet : openBottomSheet
  const closeErrorSheet = isRecommendationSheetSource ? closeNestedBottomSheet : closeBottomSheet
  const fetchFundExecution = useFetchFundExecutionWithLoading()

  async function onSell({
    isin,
    amount,
    fundName = '',
    currentValue,
    isFullWithdrawal
  }: PlanSellActionInput) {
    trackEvent({
      eventName: EventNames.ButtonTap,
      payload: {
        ScreenName: analyticsScreen,
        eventId: PlansExecutionEventIds.Sell,
        buttonLabel: 'Sell',
        ...(fundName ? { fundName } : {}),
        ...(isin ? { fundIsin: isin } : {})
      }
    })
    const resolvedIsFullWithdrawal =
      isFullWithdrawal ?? isPlanFullSell(amount, currentValue ?? 0)
    const data = await fetchFundExecution(isin)
    if (!data) {
      toastFundExecutionError(showToast)
      return
    }

    if (data?.executionOnboardingStatus === OnboardingStatus.NOT_ONBOARDED) {
      postPlanCta(data?.cta, showToast)
      return
    }

    const isRegularPlan =
      data?.buttonDisabledStates?.learnMoreReason === ExecutionLearnMoreReason.REGULAR ||
      data?.buttonDisabledStates?.sell?.disabledReason === SubActionDisabledReason.REGULAR

    if (isRegularPlan) {
      openErrorSheet(
        <RegularFundNotSupportedBottomSheet
          onDismiss={closeErrorSheet}
          analyticsScreen={analyticsScreen}
          fundName={fundName}
          fundIsin={isin}
        />,
        closeErrorSheet
      )
      return
    }

    if (transactionCta == null || typeof transactionCta !== 'object') {
      postPlanCta(null, showToast)
      return
    }

    const inputData = {
      isin,
      amount: String(amount),
      recommendationAction: LumpsumRecommendationActionType.SELL,
      isFullWithdrawal: resolvedIsFullWithdrawal
    }
    const filledCta = {
      ...(transactionCta as Record<string, unknown>),
      requestPayload: {
        inputData
      }
    }
    console.log('[plans-v4] filled execution CTA', filledCta)
    postPlanCta(filledCta, showToast)
  }

  return { onSell }
}
